import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocale, type MessageKey } from '../i18n/useLocale'
import { loadSession } from '../auth/session'
import { ensureLiveSession, redirectToSignIn } from '../auth/sessionExpiry'
import {
  SUPPORT_MESSAGE_MAX, SUPPORT_TOPICS, buildSupportBody, defaultTopicFor, openSupportForm,
  postSupportMessage, subscribeSupportForm, supportApiBase, supportAvailable, supportContextFromLocation,
  type SupportOpenRequest, type SupportResult, type SupportTopic,
} from '../data/support'
import { showNotWiredToast } from '../lib/notWiredToast'

/*
 * The in-app "Message support" form (CMT-2026-10-31-002). Mounted ONCE at the
 * app root; every entry point opens it through openSupportForm() with its own
 * prefill (src/data/support.ts). It posts to settings' /api/participant/support
 * with the ID token, shows the SUP- reference on a 201, and keeps the draft on
 * every other answer: nothing is assumed delivered unless the route said 201.
 */

type Phase =
  | { kind: 'edit' }
  | { kind: 'sending' }
  | { kind: 'sent'; reference: string | null }

type Problem = Exclude<SupportResult, { kind: 'sent' }> | { kind: 'signed_out' }

export function SupportFormHost({ fetchImpl }: { fetchImpl?: typeof fetch } = {}) {
  const [open, setOpen] = useState<{ req: SupportOpenRequest; seq: number } | null>(null)
  useEffect(() => subscribeSupportForm(r => {
    // The page context is read at open time; the entry point's own fields win.
    const merged: SupportOpenRequest = { ...supportContextFromLocation() }
    for (const [k, v] of Object.entries(r)) if (v !== undefined) (merged as Record<string, unknown>)[k] = v
    setOpen(prev => ({ req: merged, seq: (prev?.seq ?? 0) + 1 }))
  }), [])
  if (!open) return null
  // keyed by open count so each open starts a fresh draft
  return <SupportFormDialog key={open.seq} req={open.req} fetchImpl={fetchImpl} onClose={() => setOpen(null)} />
}

/**
 * An inline "Contact support" link that opens the support form with `request`
 * as the prefill. Renders nothing when the support host is not configured,
 * so an entry point is never a dead link.
 */
export function SupportLink({ testid, label, request, style, env }: {
  testid: string
  label: string
  request?: SupportOpenRequest
  style?: CSSProperties
  env?: Record<string, string | undefined>
}) {
  if (!supportAvailable(env)) return null
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => { if (!openSupportForm(request, env)) showNotWiredToast() }}
      style={{
        border: 0, background: 'transparent', padding: 0, font: 'inherit', fontWeight: 700,
        color: 'var(--tru-status-teal-bright)', cursor: 'pointer', textAlign: 'start', ...style,
      }}
    >
      {label}
    </button>
  )
}

function SupportFormDialog({ req, fetchImpl, onClose }: {
  req: SupportOpenRequest
  fetchImpl?: typeof fetch
  onClose: () => void
}) {
  const { t, dir } = useLocale()
  const [topic, setTopic] = useState<SupportTopic>(req.topic ?? defaultTopicFor(req.step))
  // GENERAL entry (CMT-2026-10-31-005 b): the event is the customer's answer
  // only — '' = not chosen yet, 'none' = my account / something else, else a
  // choice index. A ticket entry (no eventChoices) keeps its own eventId.
  const asksEvent = req.eventChoices !== undefined
  const [eventPick, setEventPick] = useState('')
  const picked = asksEvent && eventPick !== '' && eventPick !== 'none' ? req.eventChoices![Number(eventPick)] : undefined
  const ctx = asksEvent ? { ...req, eventId: picked?.eventId, ticketLabel: picked?.ticketLabel, orderRef: picked?.orderRef ?? req.orderRef } : req
  const [message, setMessage] = useState((req.message ?? '').slice(0, SUPPORT_MESSAGE_MAX))
  const [phase, setPhase] = useState<Phase>({ kind: 'edit' })
  const [problem, setProblem] = useState<Problem | null>(() => (loadSession() ? null : { kind: 'signed_out' }))
  const messageRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    messageRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const send = async () => {
    if (phase.kind === 'sending') return
    if (asksEvent && eventPick === '') {
      setProblem({ kind: 'invalid', field: 'event_id', code: 'required' })
      return
    }
    if (!message.trim()) {
      setProblem({ kind: 'invalid', field: 'message', code: 'required' })
      return
    }
    const base = supportApiBase()
    if (!base) {
      setProblem({ kind: 'unavailable' })
      return
    }
    setPhase({ kind: 'sending' })
    setProblem(null)
    const health = await ensureLiveSession()
    const session = loadSession()
    if (health === 'dead' || !session) {
      setPhase({ kind: 'edit' })
      setProblem({ kind: 'auth', identity: false })
      return
    }
    const result = await postSupportMessage(base, session.idToken, buildSupportBody(topic, message, ctx), fetchImpl ?? fetch)
    if (result.kind === 'sent') {
      setPhase({ kind: 'sent', reference: result.reference })
      req.onSent?.(result.reference)
      return
    }
    setPhase({ kind: 'edit' })
    setProblem(result)
  }

  const problemText = (p: Problem): string => {
    switch (p.kind) {
      case 'signed_out': return t('support.form.signedOut')
      case 'auth': return t(p.identity ? 'support.form.err.identity' : 'support.form.err.auth')
      case 'rate_limited':
        return p.retryAfterSec
          ? t('support.form.err.rateLimited', { minutes: String(Math.max(1, Math.ceil(p.retryAfterSec / 60))) })
          : t('support.form.err.rateLimitedNoTime')
      case 'unavailable': return t('support.form.err.unavailable')
      case 'invalid':
        if (p.field === 'event_id' && p.code === 'required') return t('support.form.err.eventRequired')
        if (p.field === 'message' && p.code === 'required') return t('support.form.err.required')
        if (p.field === 'message' && p.code === 'too_long') return t('support.form.err.tooLong', { max: String(SUPPORT_MESSAGE_MAX) })
        return t('support.form.err.invalid')
      default: return t('support.form.err.generic')
    }
  }

  const ctxItems = [
    ctx.eventId ? t('support.form.ctx.event') : null,
    req.orderRef ? t('support.form.ctx.order', { ref: req.orderRef }) : null,
    req.url ? t('support.form.ctx.page') : null,
  ].filter(Boolean).join(', ')

  const needsSignIn = problem?.kind === 'signed_out' || problem?.kind === 'auth'
  const sending = phase.kind === 'sending'

  const field: CSSProperties = {
    width: '100%', boxSizing: 'border-box', borderRadius: 11, border: '1px solid var(--hairline-14)',
    background: 'var(--surface, transparent)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5,
    padding: '10px 12px',
  }
  const label: CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 800, marginBottom: 6 }
  const primary: CSSProperties = {
    minHeight: 44, padding: '0 18px', borderRadius: 'var(--r-btn, 12px)', border: '1px solid transparent',
    background: 'var(--accent)', color: 'var(--accent-ink, var(--accent-on))', fontFamily: 'inherit',
    fontSize: 13, fontWeight: 800, cursor: 'pointer',
  }
  const secondary: CSSProperties = {
    ...primary, background: 'transparent', color: 'var(--text)', border: '1px solid var(--hairline-14)',
  }

  return (
    <div
      data-testid="support-form-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        id="support.form"
        data-testid="support-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-form-title"
        dir={dir}
        style={{
          width: '100%', maxWidth: 460, maxHeight: 'calc(100vh - 32px)', overflow: 'auto', boxSizing: 'border-box',
          background: 'var(--bg-help, var(--bg))', border: '1px solid var(--hairline-12)', borderRadius: 16,
          padding: '18px 18px 16px', color: 'var(--text)', boxShadow: '0 18px 44px rgba(0,0,0,.45)',
        }}
      >
        <div id="support-form-title" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 4 }}>
          {phase.kind === 'sent' ? t('support.form.sent.title') : t('support.form.title')}
        </div>

        {phase.kind === 'sent' ? (
          <div data-testid="support-form-sent" role="status">
            {phase.reference ? (
              <>
                <p style={{ fontSize: 13, lineHeight: 1.5, margin: '8px 0 4px' }}>
                  {t('support.form.sent.refLabel')}{' '}
                  <strong data-testid="support-form-ref" style={{ fontFamily: "'Space Mono', monospace" }}>{phase.reference}</strong>
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>{t('support.form.sent.body')}</p>
              </>
            ) : (
              <p style={{ fontSize: 13, lineHeight: 1.5, margin: '8px 0 0' }}>{t('support.form.sent.noRef')}</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button data-testid="support-form-done" onClick={onClose} style={primary}>{t('support.form.close')}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); void send() }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 14px' }}>{t('support.form.intro')}</p>

            <label htmlFor="support-form-topic" style={label}>{t('support.form.topic')}</label>
            <select
              id="support-form-topic"
              data-testid="support-form-topic"
              value={topic}
              onChange={e => setTopic(e.target.value as SupportTopic)}
              style={{ ...field, marginBottom: 12, minHeight: 44 }}
            >
              {SUPPORT_TOPICS.map(k => (
                <option key={k} value={k}>{t(`support.form.topic.${k}` as MessageKey)}</option>
              ))}
            </select>

            {asksEvent && (
              <>
                <label htmlFor="support-form-event" style={label}>{t('support.form.event')}</label>
                <select
                  id="support-form-event"
                  data-testid="support-form-event"
                  value={eventPick}
                  onChange={e => { setEventPick(e.target.value); if (problem?.kind === 'invalid' && problem.field === 'event_id') setProblem(null) }}
                  style={{ ...field, marginBottom: 12, minHeight: 44 }}
                >
                  <option value="" disabled>{t('support.form.event.choose')}</option>
                  {req.eventChoices!.map((c, i) => <option key={`${c.eventId}|${i}`} value={String(i)}>{c.label}</option>)}
                  <option value="none">{t('support.form.event.none')}</option>
                </select>
              </>
            )}

            <label htmlFor="support-form-message" style={label}>{t('support.form.message')}</label>
            <textarea
              id="support-form-message"
              ref={messageRef}
              data-testid="support-form-message"
              value={message}
              maxLength={SUPPORT_MESSAGE_MAX}
              rows={6}
              onChange={e => setMessage(e.target.value)}
              style={{ ...field, resize: 'vertical', minHeight: 120 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 10.5, color: 'var(--text-dim)', marginTop: 4 }}>
              <span data-testid="support-form-context">{ctxItems ? t('support.form.context', { items: ctxItems }) : ''}</span>
              <span style={{ flex: 'none' }}>{t('support.form.count', { n: String(message.length), max: String(SUPPORT_MESSAGE_MAX) })}</span>
            </div>

            {problem && (
              <div
                data-testid="support-form-error"
                data-error-kind={problem.kind}
                role="alert"
                style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.5, fontWeight: 700,
                  background: 'rgba(230,193,90,.1)', border: '1px solid rgba(230,193,90,.28)',
                  color: 'var(--tru-status-amber, var(--warn))',
                }}
              >
                {problemText(problem)}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              <button type="button" data-testid="support-form-cancel" onClick={onClose} style={secondary}>{t('support.form.cancel')}</button>
              {needsSignIn ? (
                <button type="button" data-testid="support-form-signin" onClick={() => redirectToSignIn()} style={primary}>{t('support.form.signIn')}</button>
              ) : (
                <button
                  type="submit"
                  data-testid="support-form-send"
                  disabled={sending}
                  aria-busy={sending || undefined}
                  style={{ ...primary, opacity: sending ? 0.7 : 1, cursor: sending ? 'default' : 'pointer' }}
                >
                  {sending ? t('support.form.sending') : t('support.form.send')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
